<template>
  <el-dialog
    :visible="true"
    fullscreen
    append-to-body
    :show-close="false"
    custom-class="add-payment-modal"
    @close="$emit('close')"
  >
    <!-- Header -->
    <div slot="title" class="apm-header">
      <div class="apm-header__left">
        <i class="el-icon-wallet apm-header__icon"></i>
        <div>
          <h3 class="apm-header__title">Cobro de factura</h3>
          <span class="apm-header__subtitle">
            #{{ invoiceNumber
            }}<template v-if="clientName"> · {{ clientName }}</template>
          </span>
        </div>
      </div>
      <button class="apm-header__close" @click="$emit('close')">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <!-- Tabs -->
    <div class="apm-tabs">
      <button
        class="apm-tab"
        :class="{ 'apm-tab--active': activeTab === 'mark' }"
        @click="activeTab = 'mark'"
      >
        <i class="el-icon-circle-check"></i>
        Marcar como pagada
      </button>
      <button
        class="apm-tab"
        :class="{ 'apm-tab--active': activeTab === 'partial' }"
        @click="activeTab = 'partial'"
      >
        <i class="el-icon-coin"></i>
        Agregar pago
      </button>
    </div>

    <!-- Placeholder when no tab selected -->
    <div v-if="activeTab === null" class="apm-placeholder">
      <i class="el-icon-wallet"></i>
      <p>Selecciona una opción para continuar</p>
    </div>

    <!-- Body -->
    <div v-if="activeTab !== null" class="apm-body" v-loading="loadingPayments">
      <div class="apm-layout">
        <!-- Left: Form -->
        <div class="apm-left">
          <!-- Existing payments -->
          <div v-if="existingPayments.length > 0" class="apm-section">
            <h4 class="apm-section__title">Pagos registrados</h4>
            <div class="payments-history">
              <div
                v-for="p in existingPayments"
                :key="p.id"
                class="payment-history-row"
                :class="{
                  'payment-history-row--editing': editingPaymentId === p.id
                }"
              >
                <span class="phi-date">{{ formatDate(p.payment_date) }}</span>
                <span class="phi-method">{{
                  methodLabel(p.payment_method)
                }}</span>
                <span class="phi-amount"
                  >${{ formatNum(p.amount_received) }}</span
                >
                <span v-if="p.retention_total > 0" class="phi-retention">
                  ret. ${{ formatNum(p.retention_total) }}
                </span>
                <span class="phi-actions">
                  <button
                    type="button"
                    class="phi-btn"
                    title="Editar pago"
                    @click="startEdit(p)"
                  >
                    <i class="el-icon-edit"></i>
                  </button>
                  <button
                    type="button"
                    class="phi-btn phi-btn--danger"
                    title="Eliminar pago"
                    @click="confirmDelete(p)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>

          <!-- Edit-mode banner -->
          <div v-if="editingPaymentId" class="apm-edit-banner">
            <i class="el-icon-edit"></i>
            <span
              >Editando un pago registrado — modifica los campos y guarda los
              cambios.</span
            >
            <button
              type="button"
              class="apm-edit-banner__cancel"
              @click="cancelEdit"
            >
              Cancelar edición
            </button>
          </div>

          <!-- Retention -->
          <div class="apm-section">
            <div class="apm-section__head">
              <h4 class="apm-section__title">Retenciones</h4>
              <button type="button" class="ret-add-btn" @click="addRetention()">
                <i class="el-icon-plus"></i> Agregar retención
              </button>
            </div>

            <div class="retention-presets">
              <span
                v-for="preset in retentionPresets"
                :key="preset.label"
                class="preset-chip"
                :class="{
                  'preset-chip--active': activePreset === preset.label
                }"
                @click="applyPreset(preset)"
                >{{ preset.label }}</span
              >
            </div>

            <div v-if="retentions.length === 0" class="ret-empty">
              Sin retenciones. Usa un atajo o "Agregar retención".
            </div>

            <div v-for="(row, idx) in retentions" :key="idx" class="ret-row">
              <div class="ret-row__main">
                <el-select
                  v-model="row.tax_type"
                  class="ret-type"
                  @change="onTypeChange(row)"
                >
                  <el-option label="Renta" value="renta"></el-option>
                  <el-option label="IVA" value="iva"></el-option>
                  <el-option label="Otro" value="otro"></el-option>
                </el-select>

                <el-autocomplete
                  v-model="row.query"
                  class="ret-search"
                  :fetch-suggestions="(q, cb) => querySearch(row, q, cb)"
                  :trigger-on-focus="row.tax_type !== 'otro'"
                  :placeholder="
                    row.tax_type === 'otro'
                      ? 'Concepto (manual)'
                      : 'Buscar código SRI'
                  "
                  suffix-icon="el-icon-search"
                  @select="item => onSelectTax(row, item)"
                >
                  <template slot-scope="{ item }">
                    <div class="ret-opt">
                      <strong>{{ item.annex }}</strong> · {{ item.title }}
                      <span class="ret-opt__pct">{{ item.percentage }}%</span>
                    </div>
                  </template>
                </el-autocomplete>

                <button
                  type="button"
                  class="ret-remove"
                  title="Quitar"
                  @click="removeRetention(idx)"
                >
                  <i class="el-icon-close"></i>
                </button>
              </div>

              <div class="ret-row__calc">
                <div class="ret-field">
                  <label>Base imponible</label>
                  <el-input
                    v-model.number="row.base_amount"
                    type="number"
                    min="0"
                    @input="calculate"
                  >
                    <template slot="prepend">$</template>
                  </el-input>
                </div>
                <div class="ret-field ret-field--pct">
                  <label>%</label>
                  <el-input
                    v-model.number="row.percentage"
                    type="number"
                    min="0"
                    max="100"
                    @input="onManualPct(row)"
                  />
                </div>
                <div class="ret-field ret-field--amount">
                  <label>Retenido</label>
                  <div class="ret-amount">${{ formatNum(rowAmount(row)) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment methods -->
          <div class="apm-section">
            <h4 class="apm-section__title">Forma de cobro</h4>
            <div class="payment-methods">
              <div
                v-for="method in paymentMethods"
                :key="method.value"
                class="pmethod"
                :class="{
                  'pmethod--active': form.payment_method === method.value
                }"
                @click="form.payment_method = method.value"
              >
                <i :class="method.icon" class="pmethod__icon"></i>
                <span class="pmethod__label">{{ method.label }}</span>
              </div>
            </div>
          </div>

          <!-- "Agregar pago" extras: amount + date + notes -->
          <template v-if="activeTab === 'partial'">
            <div class="apm-section">
              <div class="apm-row">
                <div class="apm-field">
                  <label class="apm-field__label apm-field__label--required"
                    >Monto recibido</label
                  >
                  <el-input
                    v-model.number="form.amount_received"
                    type="number"
                    min="0"
                    placeholder="0.00"
                    @input="calculate"
                  >
                    <template slot="prepend">$</template>
                  </el-input>
                </div>
                <div class="apm-field">
                  <label class="apm-field__label apm-field__label--required"
                    >Fecha de cobro</label
                  >
                  <el-date-picker
                    v-model="form.payment_date"
                    type="date"
                    placeholder="Fecha"
                    format="dd/MM/yyyy"
                    value-format="yyyy-MM-dd"
                    style="width:100%"
                  />
                </div>
              </div>
              <div class="apm-field">
                <label class="apm-field__label">Notas (opcional)</label>
                <el-input
                  v-model="form.notes"
                  type="textarea"
                  :rows="2"
                  placeholder="Referencia de transferencia, número de cheque, etc."
                />
              </div>
            </div>
          </template>

          <!-- "Marcar como pagada" extras: date + notes -->
          <template v-if="activeTab === 'mark'">
            <div class="apm-section">
              <div class="apm-row">
                <div class="apm-field">
                  <label class="apm-field__label apm-field__label--required"
                    >Fecha de cobro</label
                  >
                  <el-date-picker
                    v-model="form.payment_date"
                    type="date"
                    placeholder="Fecha"
                    format="dd/MM/yyyy"
                    value-format="yyyy-MM-dd"
                    style="width:100%"
                  />
                </div>
                <div class="apm-field"><!-- spacer --></div>
              </div>
              <div class="apm-field">
                <label class="apm-field__label">Notas (opcional)</label>
                <el-input
                  v-model="form.notes"
                  type="textarea"
                  :rows="2"
                  placeholder="Referencia de transferencia, número de cheque, etc."
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Right: Summary -->
        <div class="apm-right">
          <div class="apm-summary">
            <h4 class="apm-summary__title">Resumen</h4>

            <div class="sum-row">
              <span>Total facturado</span>
              <span>${{ formatNum(invoiceTotal) }}</span>
            </div>

            <template v-if="existingPayments.length > 0">
              <div class="sum-row sum-row--secondary">
                <span>Pagos anteriores</span>
                <span>- ${{ formatNum(totalPreviouslyPaid) }}</span>
              </div>
              <div class="sum-row sum-row--secondary">
                <span>Retenciones anteriores</span>
                <span>- ${{ formatNum(totalPreviousRetentions) }}</span>
              </div>
              <div class="sum-divider"></div>
              <div class="sum-row sum-row--balance">
                <span>Saldo pendiente</span>
                <span
                  :class="previousBalance > 0 ? 'text-orange' : 'text-green'"
                >
                  ${{ formatNum(previousBalance) }}
                </span>
              </div>
              <div class="sum-divider"></div>
            </template>

            <div v-if="calc.retentionTotal > 0">
              <div
                v-for="(row, idx) in retentions"
                :key="idx"
                class="sum-row sum-row--retention"
              >
                <span v-if="rowAmount(row) > 0">
                  Ret. {{ retentionLabel(row) }} ({{ row.percentage || 0 }}%)
                </span>
                <span v-if="rowAmount(row) > 0"
                  >- ${{ formatNum(rowAmount(row)) }}</span
                >
              </div>
              <div class="sum-row sum-row--retention sum-row--retention-total">
                <span>Total retenciones</span>
                <span>- ${{ formatNum(calc.retentionTotal) }}</span>
              </div>
              <div class="sum-divider"></div>
            </div>

            <div class="sum-row sum-row--net">
              <span><strong>Neto esperado</strong></span>
              <span class="text-green"
                ><strong>${{ formatNum(calc.netExpected) }}</strong></span
              >
            </div>

            <template v-if="activeTab === 'partial'">
              <div class="sum-row">
                <span>Monto a recibir ahora</span>
                <span>${{ formatNum(form.amount_received || 0) }}</span>
              </div>
              <div class="sum-divider"></div>
              <div
                class="sum-row sum-row--final"
                :class="
                  calc.newBalance > 0 ? 'sum-row--pending' : 'sum-row--done'
                "
              >
                <span><strong>Saldo restante</strong></span>
                <span
                  ><strong>${{ formatNum(calc.newBalance) }}</strong></span
                >
              </div>
            </template>
            <template v-else>
              <div class="sum-divider"></div>
              <div class="sum-row sum-row--final sum-row--done">
                <span><strong>Saldo restante</strong></span>
                <span><strong>$0.00</strong></span>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- /.apm-layout -->
    </div>

    <!-- Footer -->
    <div slot="footer" class="apm-footer">
      <span
        v-if="activeTab === 'partial' && validationHint"
        class="apm-footer__hint"
      >
        <i class="el-icon-warning-outline"></i> {{ validationHint }}
      </span>
      <el-button @click="editingPaymentId ? cancelEdit() : $emit('close')">{{
        editingPaymentId ? "Cancelar edición" : "Cancelar"
      }}</el-button>
      <el-button
        v-if="activeTab === 'partial'"
        type="primary"
        :loading="saving"
        :disabled="!canSavePartial"
        @click="editingPaymentId ? saveEdit() : confirmPayment()"
        >{{
          editingPaymentId ? "Guardar cambios" : "Registrar pago"
        }}</el-button
      >
      <el-button
        v-else-if="activeTab === 'mark'"
        type="primary"
        :loading="saving"
        :disabled="!form.payment_date"
        @click="confirmMarkAsPaid"
        >Marcar como pagada</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
import projectService from "../services/projectService";
import SriServices from "@/services/SriServices";

export default {
  name: "PaymentModal",
  props: {
    invoice: { type: Object, required: true },
    // 'partial' abre directo a la gestión de pagos (editar/eliminar + agregar);
    // null muestra el placeholder de selección.
    openTab: { type: String, default: null }
  },
  data() {
    return {
      activeTab: null,
      editingPaymentId: null,
      form: {
        payment_method: "transfer",
        amount_received: null,
        payment_date: new Date().toISOString().slice(0, 10),
        notes: ""
      },
      // Dynamic retention lines (1..N). Each: { tax_type, sri_withholding_tax_id,
      // label, sri_code, base_amount, percentage, query }
      retentions: [],
      sriTaxes: [],
      activePreset: "Sin retención",
      existingPayments: [],
      loadingPayments: false,
      calc: {
        retentionTotal: 0,
        netExpected: 0,
        newBalance: 0
      },
      retentionPresets: [
        { label: "Sin retención", renta: 0, iva: 0 },
        { label: "1% + 70% IVA", renta: 1, iva: 70 },
        { label: "1% + 100% IVA", renta: 1, iva: 100 },
        { label: "2% + 70% IVA", renta: 2, iva: 70 },
        { label: "2% + 100% IVA", renta: 2, iva: 100 }
      ],
      paymentMethods: [
        {
          value: "transfer",
          label: "Transferencia",
          icon: "el-icon-bank-card"
        },
        { value: "cash", label: "Efectivo", icon: "el-icon-money" },
        { value: "card", label: "Tarjeta", icon: "el-icon-wallet" },
        { value: "check", label: "Cheque", icon: "el-icon-document" }
      ],
      saving: false
    };
  },
  computed: {
    invoiceNumber() {
      return (
        this.invoice.number_invoice ||
        this.invoice.number ||
        this.invoice.sequence ||
        ""
      );
    },
    clientName() {
      const c = this.invoice.client;
      if (!c) return "";
      return c.business_name || c.name || "";
    },
    invoiceTotal() {
      return parseFloat(this.invoice.total) || 0;
    },
    invoiceSubTotal() {
      return parseFloat(this.invoice.sub_total) || 0;
    },
    invoiceIvaTotal() {
      return parseFloat(this.invoice.iva_total) || 0;
    },
    totalPreviouslyPaid() {
      return this.existingPayments.reduce(
        (s, p) => s + parseFloat(p.amount_received || 0),
        0
      );
    },
    totalPreviousRetentions() {
      return this.existingPayments.reduce(
        (s, p) => s + parseFloat(p.retention_total || 0),
        0
      );
    },
    previousBalance() {
      return Math.max(
        0,
        this.invoiceTotal -
          this.totalPreviousRetentions -
          this.totalPreviouslyPaid
      );
    },
    retentionsValid() {
      return this.retentions.every(r => {
        const base = parseFloat(r.base_amount);
        const pct = parseFloat(r.percentage);
        const baseOk = isNaN(base) || base >= 0;
        const pctOk = isNaN(pct) || (pct >= 0 && pct <= 100);
        return baseOk && pctOk;
      });
    },
    canSavePartial() {
      return (
        this.form.amount_received > 0 &&
        !!this.form.payment_date &&
        this.calc.netExpected >= 0 &&
        this.retentionsValid
      );
    },
    validationHint() {
      if (
        this.form.amount_received !== null &&
        this.form.amount_received <= 0
      ) {
        return "El monto recibido debe ser mayor a 0 (no se permiten negativos).";
      }
      if (!this.retentionsValid) {
        return "Revisa las retenciones: base y porcentaje no pueden ser negativos.";
      }
      if (this.calc.netExpected < 0) {
        return "La retención no puede superar el total de la factura.";
      }
      return "";
    }
  },
  mounted() {
    if (this.openTab) this.activeTab = this.openTab;
    this.loadPayments();
    this.loadSriTaxes();
  },
  methods: {
    async loadSriTaxes() {
      try {
        const res = await SriServices.getWithholdingTaxes();
        if (res.data.success) this.sriTaxes = res.data.data || [];
      } catch (e) {
        /* catálogo opcional: el usuario aún puede ingresar % manual */
      }
    },
    async loadPayments() {
      this.loadingPayments = true;
      try {
        const res = await projectService.getPayments(this.invoice.id);
        if (res.data.success) this.existingPayments = res.data.data || [];
      } catch (e) {
        /* normal */
      } finally {
        this.loadingPayments = false;
        // Si la factura ya tiene pagos y no se forzó un tab, abrir en la
        // gestión de pagos para que editar/eliminar sea visible de inmediato.
        if (
          this.activeTab === null &&
          !this.openTab &&
          this.existingPayments.length > 0
        ) {
          this.activeTab = "partial";
        }
        this.calculate();
      }
    },
    // ── Retention helpers ──────────────────────────────────────────
    defaultBase(taxType) {
      if (taxType === "renta") return this.invoiceSubTotal;
      if (taxType === "iva") return this.invoiceIvaTotal;
      return this.invoiceTotal;
    },
    makeRow(taxType, percentage = 0) {
      return {
        tax_type: taxType,
        sri_withholding_tax_id: null,
        label: "",
        sri_code: "",
        base_amount: this.defaultBase(taxType),
        percentage: percentage || 0,
        query: ""
      };
    },
    rowAmount(row) {
      const base = parseFloat(row.base_amount) || 0;
      const pct = parseFloat(row.percentage) || 0;
      return parseFloat((base * (pct / 100)).toFixed(2));
    },
    retentionLabel(row) {
      if (row.label) return row.label;
      const m = { renta: "renta", iva: "IVA", otro: "otra" };
      return m[row.tax_type] || row.tax_type;
    },
    addRetention(taxType = "renta") {
      this.retentions.push(this.makeRow(taxType));
      this.activePreset = "";
      this.calculate();
    },
    removeRetention(idx) {
      this.retentions.splice(idx, 1);
      this.activePreset = this.retentions.length === 0 ? "Sin retención" : "";
      this.calculate();
    },
    onTypeChange(row) {
      // Reset catalog selection and base when switching type
      row.sri_withholding_tax_id = null;
      row.label = "";
      row.sri_code = "";
      row.query = "";
      row.base_amount = this.defaultBase(row.tax_type);
      this.activePreset = "";
      this.calculate();
    },
    onManualPct() {
      this.activePreset = "";
      this.calculate();
    },
    querySearch(row, queryString, cb) {
      let pool = this.sriTaxes;
      if (row.tax_type === "renta") {
        pool = pool.filter(t => String(t.type_tax) === "1");
      } else if (row.tax_type === "iva") {
        pool = pool.filter(t => String(t.type_tax) === "2");
      } else {
        pool = []; // "Otro": sin catálogo, % manual
      }
      const q = (queryString || "").toLowerCase();
      const results = q
        ? pool.filter(
            t =>
              (t.title || "").toLowerCase().indexOf(q) > -1 ||
              String(t.annex || "")
                .toLowerCase()
                .indexOf(q) > -1
          )
        : pool;
      cb(results.slice(0, 50));
    },
    onSelectTax(row, item) {
      row.sri_withholding_tax_id = item.id;
      row.percentage = parseFloat(item.percentage) || 0;
      row.label = item.title;
      row.sri_code = item.annex;
      row.query = item.title;
      this.activePreset = "";
      this.calculate();
    },
    calculate() {
      const retTotal = parseFloat(
        this.retentions.reduce((s, r) => s + this.rowAmount(r), 0).toFixed(2)
      );
      const netExpected = parseFloat((this.invoiceTotal - retTotal).toFixed(2));
      const received = parseFloat(this.form.amount_received) || 0;
      const newBalance = parseFloat(
        (this.previousBalance - retTotal - received).toFixed(2)
      );
      this.calc = { retentionTotal: retTotal, netExpected, newBalance };
    },
    applyPreset(preset) {
      this.activePreset = preset.label;
      const rows = [];
      if (preset.renta > 0) rows.push(this.makeRow("renta", preset.renta));
      if (preset.iva > 0) rows.push(this.makeRow("iva", preset.iva));
      this.retentions = rows;
      this.calculate();
    },
    buildRetentionsPayload() {
      return this.retentions
        .filter(r => this.rowAmount(r) > 0)
        .map(r => ({
          tax_type: r.tax_type,
          sri_withholding_tax_id: r.sri_withholding_tax_id,
          label: r.label || r.query || null,
          sri_code: r.sri_code || null,
          base_amount: parseFloat(r.base_amount) || 0,
          percentage: parseFloat(r.percentage) || 0
        }));
    },
    async confirmPayment() {
      if (!this.canSavePartial) return;
      this.saving = true;
      try {
        const res = await projectService.addPayment(this.invoice.id, {
          amount_received: this.form.amount_received,
          payment_date: this.form.payment_date,
          payment_method: this.form.payment_method,
          retentions: this.buildRetentionsPayload(),
          notes: this.form.notes
        });
        if (res.data.success) {
          this.$notifications.success({
            message: "Pago registrado correctamente."
          });
          this.$emit("payment-registered", {
            invoiceId: this.invoice.id,
            result: res.data.data
          });
          this.$emit("close");
        }
      } finally {
        this.saving = false;
      }
    },
    async confirmMarkAsPaid() {
      if (!this.form.payment_date) return;
      this.saving = true;
      try {
        const amount = parseFloat(
          (this.previousBalance - this.calc.retentionTotal).toFixed(2)
        );
        const res = await projectService.addPayment(this.invoice.id, {
          amount_received: amount > 0 ? amount : this.calc.netExpected,
          payment_date: this.form.payment_date,
          payment_method: this.form.payment_method,
          retentions: this.buildRetentionsPayload(),
          notes: this.form.notes,
          mark_as_paid: true
        });
        if (res.data.success) {
          this.$notifications.success({
            message: "Factura marcada como pagada."
          });
          this.$emit("payment-registered", {
            invoiceId: this.invoice.id,
            result: res.data.data
          });
          this.$emit("close");
        }
      } finally {
        this.saving = false;
      }
    },
    // ── Editar / eliminar pago registrado ──────────────────────────
    startEdit(p) {
      this.editingPaymentId = p.id;
      this.activeTab = "partial";
      this.activePreset = "";
      this.form.amount_received = parseFloat(p.amount_received) || 0;
      this.form.payment_date = p.payment_date;
      this.form.payment_method = p.payment_method || "transfer";
      this.form.notes = p.notes || "";
      this.retentions = (p.retentions || []).map(r => ({
        tax_type: r.tax_type || "otro",
        sri_withholding_tax_id: r.sri_withholding_tax_id || null,
        label: r.label || "",
        sri_code: r.sri_code || "",
        base_amount: parseFloat(r.base_amount) || 0,
        percentage: parseFloat(r.percentage) || 0,
        query: r.label || ""
      }));
      this.calculate();
    },
    cancelEdit() {
      this.editingPaymentId = null;
      this.form.amount_received = null;
      this.form.payment_method = "transfer";
      this.form.notes = "";
      this.form.payment_date = new Date().toISOString().slice(0, 10);
      this.retentions = [];
      this.activePreset = "Sin retención";
      this.calculate();
    },
    async saveEdit() {
      if (!this.canSavePartial || !this.editingPaymentId) return;
      this.saving = true;
      try {
        const res = await projectService.updatePayment(
          this.invoice.id,
          this.editingPaymentId,
          {
            amount_received: this.form.amount_received,
            payment_date: this.form.payment_date,
            payment_method: this.form.payment_method,
            retentions: this.buildRetentionsPayload(),
            notes: this.form.notes
          }
        );
        if (res.data.success) {
          this.$notifications.success({
            message: "Pago actualizado correctamente."
          });
          this.$emit("payment-registered", {
            invoiceId: this.invoice.id,
            result: res.data.data
          });
          this.cancelEdit();
          await this.loadPayments();
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        this.$notifications.error({
          message: msg || "No se pudo actualizar el pago."
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(p) {
      this.$confirm(
        "¿Eliminar este pago? Esta acción no se puede deshacer y recalculará el saldo de la factura.",
        "Eliminar pago",
        {
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          type: "warning"
        }
      )
        .then(async () => {
          this.saving = true;
          try {
            const res = await projectService.deletePayment(
              this.invoice.id,
              p.id
            );
            if (res.data.success) {
              this.$notifications.success({ message: "Pago eliminado." });
              this.$emit("payment-registered", {
                invoiceId: this.invoice.id,
                result: res.data.data
              });
              if (this.editingPaymentId === p.id) this.cancelEdit();
              await this.loadPayments();
            }
          } catch (e) {
            const msg =
              e.response && e.response.data && e.response.data.message;
            this.$notifications.error({
              message: msg || "No se pudo eliminar el pago."
            });
          } finally {
            this.saving = false;
          }
        })
        .catch(() => {});
    },
    methodLabel(val) {
      const m = {
        transfer: "Transferencia",
        cash: "Efectivo",
        card: "Tarjeta",
        check: "Cheque"
      };
      return m[val] || val;
    },
    formatNum(val) {
      if (val === null || val === undefined) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val + "T00:00:00").toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  }
};
</script>

<style lang="scss">
.add-payment-modal {
  display: flex;
  flex-direction: column;
  border-radius: 0 !important;
  margin: 0 !important;

  // Global rule hides fullscreen dialog headers — match specificity to override
  &.el-dialog.is-fullscreen .el-dialog__header {
    display: block !important;
    padding: 0 !important;
    border-bottom: none !important;
    background: transparent !important;
  }
  .el-dialog__body {
    padding: 0 !important;
    flex: 1;
    overflow-y: auto;
  }
  .el-dialog__footer {
    padding: 14px 28px 20px !important;
    border-top: 1.5px solid #e4eaf6;
    flex-shrink: 0;
  }
}
</style>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3ecfb4;
$green: #1d9e75;
$border: #e4eaf6;
$bg: #f7f8fc;

// ── Header ────────────────────────────────────────────────────────
.apm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: #fff;
  border-bottom: 1.5px solid $border;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__icon {
    font-size: 22px;
    color: $accent;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $primary;
    margin: 0 0 2px;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 12px;
    color: #aaa;
    display: block;
  }

  &__close {
    background: none;
    border: 1.5px solid $border;
    border-radius: 8px;
    width: 34px;
    height: 34px;
    font-size: 16px;
    cursor: pointer;
    color: #909399;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    flex-shrink: 0;

    &:hover {
      border-color: $primary;
      color: $primary;
      background: $bg;
    }
  }
}

// ── Tabs ──────────────────────────────────────────────────────────
.apm-tabs {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 20px 28px;
  border-bottom: 1.5px solid $border;
  background: #fff;
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: 14px 16px;
    gap: 10px;
  }
}

.apm-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 28px;
  min-width: 180px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  cursor: pointer;
  transition: all 0.18s;

  i {
    font-size: 24px;
    color: #c0c4cc;
    transition: color 0.18s;
  }

  &:hover {
    border-color: $accent;
    color: $primary;
    i {
      color: $accent;
    }
  }

  &--active {
    border-color: $accent;
    background: rgba(#3ecfb4, 0.07);
    color: $primary;
    i {
      color: $accent;
    }
  }

  @media (max-width: 480px) {
    width: auto;
    flex: 1;
    padding: 14px 8px;
    font-size: 11px;
    i {
      font-size: 20px;
    }
  }
}

// ── Placeholder ───────────────────────────────────────────────────
.apm-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #c0c4cc;

  i {
    font-size: 40px;
  }
  p {
    font-size: 14px;
    margin: 0;
  }
}

// ── Layout ────────────────────────────────────────────────────────
.apm-body {
  min-height: 0;
}

.apm-layout {
  display: flex;
  gap: 24px;
  padding: 24px 28px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
}

.apm-left {
  flex: 1;
  min-width: 0;
}

.apm-right {
  width: 280px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    order: -1; // Summary first on mobile
  }
}

// ── Two-column row ────────────────────────────────────────────────
.apm-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

// ── Sections ──────────────────────────────────────────────────────
.apm-section {
  margin-bottom: 20px;

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: #909399;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0 0 12px;
    padding-bottom: 8px;
    border-bottom: 1.5px solid $border;
  }
}

// ── Existing payments ─────────────────────────────────────────────
.payments-history {
  background: $bg;
  border: 1.5px solid $border;
  border-radius: 10px;
  overflow: hidden;
}

.payment-history-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  border-bottom: 1px solid $border;
  font-size: 13px;
  &:last-child {
    border-bottom: none;
  }
}

.phi-date {
  color: #aaa;
  font-size: 12px;
  min-width: 80px;
}
.phi-method {
  color: #666;
  flex: 1;
}
.phi-amount {
  font-weight: 700;
  color: $primary;
}
.phi-retention {
  font-size: 11px;
  color: #ef4444;
}
.phi-actions {
  display: flex;
  gap: 6px;
}
.phi-btn {
  width: 28px;
  height: 28px;
  border: 1.5px solid $border;
  background: #fff;
  border-radius: 7px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.15s;
  &:hover {
    border-color: $accent;
    color: $primary;
  }
  &--danger:hover {
    border-color: #c0392b;
    color: #c0392b;
    background: #fff5f5;
  }
}
.payment-history-row--editing {
  background: #f0fbf8;
  border-left: 3px solid $accent;
}

// ── Edit-mode banner ──────────────────────────────────────────────
.apm-edit-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: #f0fbf8;
  border: 1.5px solid $accent;
  border-radius: 10px;
  font-size: 13px;
  color: $primary;

  span {
    flex: 1;
  }
  &__cancel {
    background: none;
    border: none;
    color: #c0392b;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
  }
}

// ── Retention presets ─────────────────────────────────────────────
.retention-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.preset-chip {
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1.5px solid $border;
  color: #606266;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: $accent;
    color: $primary;
  }
  &--active {
    background: $primary;
    border-color: $primary;
    color: #fff;
    &:hover {
      color: #fff;
      border-color: $primary;
    }
  }
}

// ── Retention rows ────────────────────────────────────────────────
.apm-section__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.ret-add-btn {
  background: none;
  border: 1.5px solid $accent;
  color: $green;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  i {
    margin-right: 4px;
  }
  &:hover {
    background: $accent;
    color: #fff;
  }
}

.ret-empty {
  margin-top: 12px;
  padding: 14px;
  border: 1.5px dashed $border;
  border-radius: 10px;
  text-align: center;
  font-size: 12px;
  color: #909399;
}

.ret-row {
  margin-top: 12px;
  padding: 12px;
  border: 1.5px solid $border;
  border-radius: 10px;
  background: $bg;

  &__main {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__calc {
    display: grid;
    grid-template-columns: 1fr 90px 110px;
    gap: 10px;
    margin-top: 10px;

    @media (max-width: 480px) {
      grid-template-columns: 1fr 1fr;
    }
  }
}

.ret-type {
  width: 110px;
  flex-shrink: 0;
}

.ret-search {
  flex: 1;
}

.ret-remove {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1.5px solid $border;
  background: #fff;
  border-radius: 8px;
  color: #c0392b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #c0392b;
    background: #fff5f5;
  }
}

.ret-opt {
  font-size: 12px;
  line-height: 1.5;

  &__pct {
    color: $green;
    font-weight: 600;
    margin-left: 4px;
  }
}

.ret-field {
  display: flex;
  flex-direction: column;

  label {
    font-size: 11px;
    font-weight: 600;
    color: #909399;
    margin-bottom: 4px;
  }
}

.ret-amount {
  height: 40px;
  display: flex;
  align-items: center;
  font-weight: 700;
  color: $green;
}

// ── Payment methods ───────────────────────────────────────────────
.payment-methods {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.pmethod {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  border: 1.5px solid $border;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s;

  &__icon {
    font-size: 22px;
    color: #c0c4cc;
    transition: color 0.18s;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    color: #909399;
    transition: color 0.18s;
    text-align: center;
    line-height: 1.2;
  }

  &:hover {
    border-color: $accent;
    .pmethod__icon {
      color: $primary;
    }
    .pmethod__label {
      color: $primary;
    }
  }

  &--active {
    border-color: $accent;
    background: rgba(#3ecfb4, 0.07);

    .pmethod__icon {
      color: $primary;
    }
    .pmethod__label {
      color: $primary;
    }
  }
}

// ── Summary ───────────────────────────────────────────────────────
.apm-summary {
  background: $bg;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 18px 20px;
  position: sticky;
  top: 16px;

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: $primary;
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1.5px solid $border;
  }
}

.sum-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: #555;

  &--secondary span {
    color: #999;
    font-size: 12px;
  }
  &--balance {
    font-size: 13px;
    font-weight: 600;
  }
  &--retention span {
    color: #ef4444;
    font-size: 12px;
  }
  &--retention-total {
    font-weight: 600;
  }
  &--net {
    padding-top: 8px;
    font-size: 14px;
  }
  &--final {
    padding-top: 8px;
    font-size: 14px;
  }
  &--pending span:last-child {
    color: #ef4444;
  }
  &--done span:last-child {
    color: $green;
  }
}

.sum-divider {
  border-top: 1.5px dashed $border;
  margin: 6px 0;
}

// ── Footer ────────────────────────────────────────────────────────
.apm-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;

  &__hint {
    margin-right: auto;
    font-size: 12px;
    color: #e07b00;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
    text-align: left;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    .el-button {
      width: 100%;
    }
    &__hint {
      margin-right: 0;
    }
  }
}

// ── Form fields ───────────────────────────────────────────────────
.apm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #555;

    &--required::after {
      content: " *";
      color: #ef4444;
    }
  }
}

.apm-section .el-input-group {
  .el-input-group__prepend,
  .el-input__inner {
    display: flex;
    align-items: center;
    height: 40px;
    line-height: 40px;
  }
  .el-input__inner {
    padding-left: 14px;
  }
}

.text-green {
  color: $green !important;
}
.text-orange {
  color: #d08b00 !important;
}
</style>
